import { useDraftNavigation } from './draft-navigation';
import { useState } from 'react';
import { z } from 'zod';
import {
  accountAccessSchema,
  generateUuidV7,
  type Adviser,
  type AccountInvitationInput,
  type accountMemberSchema,
} from '@cryptology-assist/domain';
import { request, idSchema, savedSchema, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import { dateLabel, type Copy, type Language } from './copy';
import { accountBotsSchema } from './adviser-crm';

const sendersSchema = z.object({
  mailboxes: z.array(
    z.object({
      id: z.string(),
      botId: z.string(),
      botName: z.string(),
      address: z.email(),
      provider: z.string(),
    })
  ),
});
const deliverySchema = z.object({ id: z.string(), status: z.literal('sent') });
type Member = z.infer<typeof accountMemberSchema>;
type Grants = AccountInvitationInput['botGrants'];
function BotGrants({
  bots,
  value,
  copy,
  owner,
  onChange,
}: {
  bots: (typeof accountBotsSchema._output)['bots'];
  value: Grants;
  copy: Copy;
  owner: boolean;
  onChange: (value: Grants) => void;
}) {
  return (
    <section className="form-section">
      <h3>{copy.assistantAccess}</h3>
      <p>{copy.assistantAccessHelp}</p>
      {bots.map((bot) => {
        const grant = value.find((entry) => entry.botId === bot.id);
        return (
          <div className="access-grant" key={bot.id}>
            <label className="check-row">
              <input
                type="checkbox"
                checked={!!grant}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...value, { botId: bot.id, role: 'viewer' }]
                      : value.filter((entry) => entry.botId !== bot.id)
                  )
                }
              />
              {bot.name}
            </label>
            {grant && (
              <Field label={copy.accessRole}>
                <select
                  value={grant.role}
                  onChange={(event) =>
                    onChange(
                      value.map((entry) =>
                        entry.botId === bot.id
                          ? {
                              ...entry,
                              role: event.target
                                .value as Grants[number]['role'],
                            }
                          : entry
                      )
                    )
                  }
                >
                  {(owner
                    ? ['viewer', 'operator', 'admin', 'owner']
                    : ['viewer', 'operator', 'admin']
                  ).map((role) => (
                    <option key={role} value={role}>
                      {copy[role as keyof Copy]}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
        );
      })}
    </section>
  );
}
export function AccountAccess({
  companyId,
  session,
  copy,
  language,
  person,
}: {
  companyId: string;
  session: Session;
  copy: Copy;
  language: Language;
  person?: Adviser;
}) {
  const base = `/v1/accounts/${companyId}`;
  const [after, setAfter] = useState<string | null>(null);
  const remote = useRemote(
    `${base}/access${after ? `?after=${after}` : ''}`,
    accountAccessSchema
  );
  const bots = useRemote(`${base}/bots`, accountBotsSchema);
  const senders = useRemote(`${base}/sender-mailboxes`, sendersSchema);
  const owner = session.accounts.some(
    (account) => account.companyId === companyId && account.role === 'owner'
  );
  const mutation = useMutation();
  const [invite, setInvite] = useState(false);
  const [invitationId, setInvitationId] = useState(() => generateUuidV7());
  const [email, setEmail] = useState(person?.email ?? '');
  const [role, setRole] = useState<AccountInvitationInput['role']>('member');
  const [grants, setGrants] = useState<Grants>([]);
  const [sender, setSender] = useState('');
  const [editing, setEditing] = useState<Member | null>(null);
  const [sent, setSent] = useState(false);
  const [chooseExisting, setChooseExisting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [navigationError, setNavigationError] = useState<Error | null>(null);
  useDraftNavigation(dirty, async () => {
    const error = new Error('ACCESS_FORM_UNSAVED');
    setNavigationError(error);
    throw error;
  });
  return (
    <section className="form-section">
      <div className="section-toolbar">
        <h2>{copy.dashboardAccess}</h2>
        <button
          disabled={invite || !!editing}
          onClick={() => {
            setInvitationId(generateUuidV7());
            setInvite(true);
            setSent(false);
            setEditing(null);
          }}
        >
          {copy.invitePerson}
        </button>
      </div>
      <p>{copy.dashboardAccessHelp}</p>
      {person && !invite && !editing && (
        <button
          onClick={() => {
            setChooseExisting(!chooseExisting);
            setAfter(null);
          }}
        >
          {chooseExisting ? copy.showLinkedMembers : copy.linkExistingLogin}
        </button>
      )}
      <ErrorNotice
        error={
          navigationError ??
          remote.error ??
          bots.error ??
          senders.error ??
          mutation.error
        }
        copy={copy}
        onRetry={() => {
          remote.reload();
          bots.reload();
          senders.reload();
        }}
      />
      {sent && (
        <p className="notice success" role="status">
          {copy.invitationSent}
        </p>
      )}
      {remote.loading && !remote.data && <p>{copy.loading}</p>}
      {remote.data?.members
        .filter(
          (member) =>
            !person || chooseExisting || member.adviserId === person.id
        )
        .map((member) => (
          <div className="connection-row" key={member.id}>
            <div>
              <strong>{member.name}</strong>
              <p>
                {member.email} · {copy[member.provider]}
              </p>
              <span className="small muted">
                {member.role ? copy[member.role] : copy.assistantAccessOnly}
              </span>
            </div>
            <button
              disabled={invite || !!editing}
              onClick={() => {
                setEditing(member);
                setInvite(false);
              }}
            >
              {copy.editAccess}
            </button>
          </div>
        ))}
      {
        <div className="form-actions">
          {after && (
            <button disabled={dirty} onClick={() => setAfter(null)}>
              {copy.firstPage}
            </button>
          )}
          {remote.data?.nextCursor && (
            <button
              disabled={dirty}
              onClick={() => setAfter(remote.data!.nextCursor)}
            >
              {copy.nextPage}
            </button>
          )}
        </div>
      }
      {invite && bots.data && (
        <form
          className="edit-panel"
          onChange={() => {
            setDirty(true);
            setNavigationError(null);
            if (invite) setInvitationId(generateUuidV7());
          }}
          onSubmit={(event) =>
            onSubmit(event, () =>
              mutation.run(async () => {
                const mailbox = senders.data?.mailboxes.find(
                  (mailbox) => mailbox.id === sender
                );
                if (!mailbox) throw new Error('INVITATION_SENDER_REQUIRED');
                try {
                  await request(`${base}/invitations`, idSchema, {
                    csrf: session.csrf,
                    body: {
                      id: invitationId,
                      email,
                      role,
                      adviserId: person?.id ?? null,
                      botGrants: grants,
                      senderBotId: mailbox.botId,
                      senderConnectionId: mailbox.id,
                      locale: language,
                    },
                  });
                  setInvite(false);
                  setDirty(false);
                  setNavigationError(null);
                  await request(
                    `${base}/invitations/${invitationId}/delivery`,
                    deliverySchema,
                    { csrf: session.csrf, body: {} }
                  );
                  setSent(true);
                } finally {
                  remote.reload();
                }
              })
            )
          }
        >
          <fieldset disabled={mutation.busy}>
            <h3>{copy.invitePerson}</h3>
            <div className="form-grid">
              <Field label={copy.invitationEmail}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
              <Field label={copy.accountRole}>
                <select
                  value={role}
                  onChange={(event) =>
                    setRole(event.target.value as typeof role)
                  }
                >
                  {(owner
                    ? ['member', 'admin', 'owner']
                    : ['member', 'admin']
                  ).map((role) => (
                    <option key={role} value={role}>
                      {copy[role as keyof Copy]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <p className="small muted">{copy.accountRoleHelp}</p>
            <BotGrants
              bots={bots.data.bots}
              value={grants}
              onChange={setGrants}
              copy={copy}
              owner={owner}
            />
            <Field label={copy.invitationSender}>
              <select
                required
                value={sender}
                onChange={(event) => setSender(event.target.value)}
              >
                <option value="">{copy.selectMailbox}</option>
                {senders.data?.mailboxes.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.address} · {mailbox.botName}
                  </option>
                ))}
              </select>
            </Field>
            {senders.data?.mailboxes.length === 0 && (
              <p className="notice-inline">{copy.invitationSenderHelp}</p>
            )}
            <p>{copy.invitationConfirmation}</p>
            <div className="form-actions">
              <button className="primary" disabled={!sender}>
                {copy.sendInvitation}
              </button>
              <button
                type="button"
                onClick={() => {
                  setInvite(false);
                  setDirty(false);
                  setNavigationError(null);
                }}
              >
                {copy.cancel}
              </button>
            </div>
          </fieldset>
        </form>
      )}
      {editing && bots.data && (
        <form
          className="edit-panel"
          onChange={() => {
            setDirty(true);
            setNavigationError(null);
            if (invite) setInvitationId(generateUuidV7());
          }}
          onSubmit={(event) =>
            onSubmit(event, () =>
              mutation.run(async () => {
                await request(`${base}/access`, savedSchema, {
                  csrf: session.csrf,
                  body: {
                    accountId: editing.id,
                    role: editing.role,
                    adviserId: editing.adviserId,
                    botGrants: editing.botGrants,
                  },
                });
                setEditing(null);
                setDirty(false);
                setNavigationError(null);
                remote.reload();
              })
            )
          }
        >
          <fieldset disabled={mutation.busy}>
            <h3>{editing.name}</h3>
            <Field label={copy.accountRole}>
              <select
                value={editing.role ?? ''}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    role: event.target.value
                      ? (event.target.value as Member['role'])
                      : null,
                  })
                }
              >
                <option value="">{copy.assistantAccessOnly}</option>
                {(owner
                  ? ['member', 'admin', 'owner']
                  : ['member', 'admin']
                ).map((role) => (
                  <option key={role} value={role}>
                    {copy[role as keyof Copy]}
                  </option>
                ))}
              </select>
            </Field>
            <p className="small muted">{copy.accountRoleHelp}</p>
            <BotGrants
              bots={bots.data.bots}
              value={editing.botGrants}
              onChange={(botGrants) => setEditing({ ...editing, botGrants })}
              copy={copy}
              owner={owner}
            />
            {person && (
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={editing.adviserId === person.id}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      adviserId: event.target.checked ? person.id : null,
                    })
                  }
                />
                {copy.linkAdviserLogin} · {person.name}
              </label>
            )}
            <div className="form-actions">
              <button className="primary">{copy.saveAccess}</button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setDirty(false);
                  setNavigationError(null);
                }}
              >
                {copy.cancel}
              </button>
            </div>
          </fieldset>
        </form>
      )}
      {remote.data?.invitations
        .filter((invitation) => !person || invitation.adviserId === person.id)
        .map((invitation) => (
          <div className="connection-row" key={invitation.id}>
            <div>
              <strong>{invitation.email}</strong>
              <p>{copy[`invitation_${invitation.status}` as keyof Copy]}</p>
              <span className="small muted">
                {copy.expiresAt} {dateLabel(invitation.expiresAt, language)}
              </span>
            </div>
            <div className="row-actions">
              {[
                'queued',
                'failed',
                'uncertain',
                'sending',
                'preparing',
                'prepared',
              ].includes(invitation.status) && (
                <button
                  disabled={mutation.busy}
                  onClick={() =>
                    void mutation.run(async () => {
                      try {
                        await request(
                          `${base}/invitations/${invitation.id}/delivery`,
                          deliverySchema,
                          { csrf: session.csrf, body: {} }
                        );
                      } finally {
                        remote.reload();
                      }
                    })
                  }
                >
                  {invitation.status === 'uncertain' ||
                  invitation.status === 'sending'
                    ? copy.checkInvitationDelivery
                    : copy.retry}
                </button>
              )}
              <button
                disabled={mutation.busy}
                onClick={() =>
                  void mutation.run(async () => {
                    await request(
                      `${base}/invitations/${invitation.id}`,
                      savedSchema,
                      { method: 'DELETE', csrf: session.csrf, body: {} }
                    );
                    remote.reload();
                  })
                }
              >
                {copy.revokeInvitation}
              </button>
            </div>
          </div>
        ))}
    </section>
  );
}
